import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ArrowRight, CheckCircle, Clock, Lock, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AppShell } from "@/components/AppShell";
import { Spinner } from "@/components/Spinner";
import { api, apiError, FRAMEWORK_LABELS, FRAMEWORK_KEYS } from "@/lib/api";
import { toast } from "sonner";

const scoreColor = (s) => (s >= 80 ? "text-sky-700" : s >= 60 ? "text-teal-700" : s >= 40 ? "text-amber-700" : "text-rose-700");

export default function Dashboard() {
  const [products, setProducts] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const load = () =>
    api
      .get("/products")
      .then(({ data }) => setProducts(data.products))
      .catch((e) => {
        toast.error(apiError(e));
        setProducts([]);
      });

  useEffect(() => {
    load();
  }, []);

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      const { data } = await api.delete(`/products/${pendingDelete.product_id}`);
      toast.success(`${data.product_name} deleted`);
      setPendingDelete(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDeleting(false);
    }
  };

  if (products === null)
    return (
      <AppShell breadcrumb="dashboard">
        <Spinner label="Loading your products" />
      </AppShell>
    );

  return (
    <AppShell breadcrumb="dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline text-navy-800">Product portfolio</p>
          <h1 className="mt-2 font-display text-3xl font-extrabold text-navy-900">Your products</h1>
          <p className="mt-2 text-sm text-slate-600">
            Indicative readiness per product across EU CRA, IEC 62443-4-1 and IEC 62443-4-2.
          </p>
        </div>
        <Button
          onClick={() => navigate("/products/new")}
          className="bg-navy-800 text-white hover:bg-navy-700"
          data-testid="add-product-button"
        >
          <Plus size={16} className="mr-2" /> Add product
        </Button>
      </div>

      {products.length === 0 ? (
        <div className="mt-10 border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="empty-state">
          <h2 className="font-display text-lg font-bold text-navy-900">No products yet</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">
            Add your first product profile to see which frameworks apply and start the guided
            assessment.
          </p>
          <Button
            className="mt-6 bg-navy-800 text-white hover:bg-navy-700"
            onClick={() => navigate("/products/new")}
            data-testid="empty-add-product-button"
          >
            Add your first product
          </Button>
        </div>
      ) : (
        <div className="mt-8 grid gap-4 lg:grid-cols-2" data-testid="product-list">
          {products.map((p) => {
            const a = p.assessment;
            const pct = a && a.total ? Math.round((a.answered / a.total) * 100) : 0;
            const completed = a?.status === "completed";
            return (
              <div
                key={p.product_id}
                className="border border-slate-200 bg-white p-6 transition-transform hover:-translate-y-0.5 hover:shadow-md"
                data-testid={`product-card-${p.product_id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="font-display text-lg font-bold text-navy-900">{p.name}</h2>
                    <p className="mt-1 font-mono text-[11px] uppercase tracking-wide text-slate-500">
                      {p.category.replace(/_/g, " ")} · {p.connectivity.replace(/_/g, " ")}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1.5 border px-2 py-1 text-[10px] font-semibold uppercase tracking-wide ${
                        completed ? "border-teal-200 bg-teal-50 text-teal-700" : "border-amber-200 bg-amber-50 text-amber-700"
                      }`}
                      data-testid={`product-status-${p.product_id}`}
                    >
                      {completed ? <CheckCircle size={12} /> : <Clock size={12} />}
                      {completed ? "Completed" : "In progress"}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPendingDelete(p)}
                      title="Delete product"
                      aria-label={`Delete ${p.name}`}
                      className="border border-slate-200 p-1.5 text-slate-400 transition-colors hover:border-rose-300 hover:text-rose-600"
                      data-testid={`delete-product-${p.product_id}`}
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-3 gap-3 border-y border-slate-200 py-4 sm:grid-cols-6">
                  {FRAMEWORK_KEYS.map((key) => {
                    const fs = a?.framework_scores?.[key];
                    return (
                      <div key={key} data-testid={`product-score-${key}-${p.product_id}`}>
                        <p className="font-mono text-[10px] uppercase tracking-wide text-slate-500">
                          {FRAMEWORK_LABELS[key]}
                        </p>
                        {!fs ? (
                          <p className="mt-1 font-display text-lg font-bold text-slate-300">-</p>
                        ) : fs.display_score ? (
                          <>
                            <p className={`mt-1 font-display text-xl font-extrabold ${scoreColor(fs.score)}`}>
                              {fs.score}%
                            </p>
                            {fs.status === "limited" && (
                              <p className="text-[9px] uppercase tracking-wide text-amber-700">Limited</p>
                            )}
                          </>
                        ) : (
                          <p className="mt-1 text-[10px] leading-tight text-slate-400">Not applicable</p>
                        )}
                      </div>
                    );
                  })}
                </div>

                {a && (
                  <>
                    <div className="mt-4 flex items-center justify-between text-xs text-slate-600">
                      <span>
                        {a.answered}/{a.total} questions answered
                      </span>
                      <span className="font-mono">{pct}%</span>
                    </div>
                    <Progress value={pct} className="mt-2 h-1.5" />
                    {a.updated_since_purchase && (
                      <p className="mt-3 border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800" data-testid={`updated-since-purchase-${p.product_id}`}>
                        Updated since purchase, the purchased report remains a snapshot. A new report
                        purchase is required for the updated answers.
                      </p>
                    )}
                    <div className="mt-5 flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant={completed ? "outline" : "default"}
                        className={completed ? "" : "bg-navy-800 text-white hover:bg-navy-700"}
                        onClick={() => navigate(`/assessment/${a.assessment_id}`)}
                        data-testid={`resume-assessment-${p.product_id}`}
                      >
                        {completed ? "Review answers" : "Resume assessment"} <ArrowRight size={14} className="ml-1.5" />
                      </Button>
                      {completed && (
                        <Button
                          size="sm"
                          className="bg-navy-800 text-white hover:bg-navy-700"
                          onClick={() => navigate(`/results/${a.assessment_id}`)}
                          data-testid={`view-results-${p.product_id}`}
                        >
                          View results
                        </Button>
                      )}
                      {a.purchased && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/report/${a.assessment_id}`)}
                          data-testid={`view-report-${p.product_id}`}
                        >
                          <Lock size={13} className="mr-1.5" /> Paid report
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent data-testid="delete-product-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {pendingDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the product profile, its assessment and every answer you recorded. It
              cannot be undone. Any report you already paid for stays on your billing record but the
              report itself will no longer be reachable.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-product">Keep product</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-rose-700 text-white hover:bg-rose-800"
              data-testid="confirm-delete-product"
            >
              {deleting ? "Deleting…" : "Delete product"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
